import { sortBy } from 'lodash';
import { DYNAMIC_VISUALISATIONS } from './dynamic-dials';
import { getStaticVisualisation, STATIC_VISUALISATIONS } from './static-dials';
import {
  BrainFunctionCategory,
  BrainFunctionVisualisation,
  RequiredVisualisationData,
  StaticBrainFunctionVisualisation
} from './types';

/**
 * Evaluate a visualisation config and generate a static config if applicable
 * 1. StaticBrainFunctionVisualisation -> StaticBrainFunctionVisualisation
 * 2. DynamicBrainFunctionVisualisation -> StaticBrainFunctionVisualisation
 *
 * @param visualisation
 * @param visualisationData
 * @returns
 */
export function genericBrainFunctionConfigToStaticBrainFunctionConfig(
  visualisation: BrainFunctionVisualisation,
  visualisationData: RequiredVisualisationData
): StaticBrainFunctionVisualisation {
  if (visualisation.type === 'static') return visualisation;
  const { getValues, scale, isRawScore } =
    visualisation.getVisualisation(visualisationData);
  return {
    label: visualisation.label,
    type: 'static',
    getValues,
    scale,
    isRawScore
  };
}

export function prepareBrainFunctionVisualisation(
  visualisation: BrainFunctionVisualisation,
  visualisationData: RequiredVisualisationData
) {
  const brainFunction = genericBrainFunctionConfigToStaticBrainFunctionConfig(
    visualisation,
    visualisationData
  );

  // Sort the data by date
  const data = sortBy(brainFunction.getValues(visualisationData), _ => _.date);

  return { brainFunction, data };
}

export const brainFunctionCategories: BrainFunctionCategory[] = [
  {
    title: 'Balance and movement',
    visualisations: [
      getStaticVisualisation('physical-function', {
        label: 'Physical Function'
      }),
      getStaticVisualisation('vestibular/balance'),
      getStaticVisualisation('pain'),
      DYNAMIC_VISUALISATIONS['vision/oculomotor'],
      getStaticVisualisation('fatigue')
    ]
  },
  {
    title: 'Mood and social self',
    visualisations: [
      getStaticVisualisation('social-function', {
        label: 'Social Function'
      }),
      DYNAMIC_VISUALISATIONS['mood'],
      getStaticVisualisation('fatigue'),
      getStaticVisualisation('pain'),
      getStaticVisualisation('vestibular/balance')
    ]
  },
  {
    title: 'Thinking and memory',
    visualisations: [
      getStaticVisualisation('cognitive-function', {
        label: 'Cognitive Function'
      }),
      getStaticVisualisation('attention/memory'),
      getStaticVisualisation('sleep'),
      DYNAMIC_VISUALISATIONS['vision/oculomotor'],
      getStaticVisualisation('pain')
    ]
  }
];

/**
 * 2025-07-16
 * TASK 419: A new brainfunction visualisation configuration is needed
 */
export const brainFunctionVisualisations_new: Record<
  'highLevel' | 'rest',
  BrainFunctionVisualisation[]
> = {
  highLevel: [
    getStaticVisualisation('physical-function', {
      label: 'Physical Function'
    }),
    getStaticVisualisation('cognitive-function', {
      label: 'Cognitive Function'
    }),
    getStaticVisualisation('social-function', {
      label: 'Social Function'
    })
  ],
  rest: [
    getStaticVisualisation('vestibular/balance'),
    DYNAMIC_VISUALISATIONS['vision/oculomotor'],
    getStaticVisualisation('attention/memory'),
    getStaticVisualisation('fatigue'),
    getStaticVisualisation('pain'),
    getStaticVisualisation('sleep'),
    DYNAMIC_VISUALISATIONS['mood']
  ]
};

/**
 * A list of all brainfunction visualisations
 */
export const brainFunctionVisualisations = [
  ...Object.values(STATIC_VISUALISATIONS),
  ...Object.values(DYNAMIC_VISUALISATIONS)
];
